<template>
  <div id="MainPage" class="tourListPage">
    <div class="tourListPage_breadcrumbsArea">
      <breadcrumbs :condition="condition" />
    </div>
    <tour-list-header v-if="!isLoadingMaster" :condition="condition" />
    <!-- z-index: 1001 はヘッダーに勝つ値、かつ同じ追従要素のリスト上部 (z-index: 1000) に影を落とせる値 -->
    <tk-sticky-item
      v-if="mobile"
      class="tourListPage_searchConditionBoxWrapper"
      :z-index="1001"
    >
      <template v-slot="{ isPinned }">
        <search-condition-box
          :class="['tourListPage_searchConditionBox', { '-pinned': isPinned }]"
          :condition="condition"
          :disabled="isLoadingMaster"
          button-text="+ 条件変更"
          icon-color="blue"
          button-theme="round-corners"
          @execute="isSearchModalOpened = true"
        />
      </template>
    </tk-sticky-item>
    <!-- z-index: 12 はツアー概要ツールチップに勝つ値、かつ同じ追従要素のリスト上部 (z-index: 11) に影を落とせる値 -->
    <tk-sticky-item
      v-else
      class="tourListPage_searchFormAreaWrapper"
      :z-index="12"
    >
      <template v-slot="{ isPinned }">
        <div :class="['tourListPage_searchFormArea', { '-pinned': isPinned }]">
          <tk-skeleton-loader
            v-if="isLoadingMaster"
            class="tourListPage_searchFormLoading"
          />
          <search-form
            v-else
            v-model="editedSearchCondition"
            class="tourListPage_searchForm"
            layout="single-line"
            theme="flat"
            size="x-large"
            icon-color="blue"
            button-color="blue"
            simple-placeholder
            :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
            departure-icon
            destination-icon
            date-icon
            passenger-icon
            put-icon-outside
            @execute="searchAgain"
          />
        </div>
      </template>
    </tk-sticky-item>
    <!-- z-index: 1000 はヘッダーに勝つ値 -->
    <!-- z-index: 11 はツアー概要ツールチップに勝つ値 -->
    <tk-sticky-item
      class="tourListPage_sortAreaWrapper"
      :top="mobile ? 65 : 57"
      :z-index="mobile ? 1000 : 11"
    >
      <template v-slot="{ isPinned }">
        <div :class="['tourListPage_sortArea', { '-pinned': isPinned }]">
          <div class="tourListPage_sortAreaInner">
            <div class="tourListPage_sortButtons">
              <tk-pulldown
                v-if="mobile"
                class="tourListPage_sortPulldown"
                :items="[
                  { label: 'おすすめ順', value: 'price_fav' },
                  { label: '料金（安い順）', value: 'price_asc' },
                  { label: '料金（高い順）', value: 'price_desc' },
                  { label: 'ホテルランク順', value: 'hotelrank_desc' },
                  { label: '人気順', value: 'pageview' },
                ]"
                :value="order"
                :disabled="isLoadingMaster"
                theme="flat"
                size="large"
                icon="filled-spin"
                icon-color="blue"
                text-color="blue"
                bold
                @change="updateOrder($event)"
              />
              <filter-button
                v-if="mobile"
                color="blue"
                :disabled="isLoadingTours"
                :checkmark="condition.hasFilters"
                @click="isFilterModalOpened = true"
              />
              <span
                v-if="mobile"
                class="tourListPage_totalCount"
                style="margin-left: auto"
              >
                該当：<span
                  class="tourListPage_totalCountNumber"
                >
                  {{ isLoadingTours ? '-' : totalTours.toLocaleString() }}
                  </span>
                件
              </span>
              <tk-button
                v-if="!mobile"
                type="button"
                :class="[
                  'tourListPage_sortButton',
                  { '-active': order === 'price_fav' },
                ]"
                :disabled="isLoadingMaster"
                @click="updateOrder('price_fav')"
              >
                おすすめ順
              </tk-button>
              <tk-custom-pulldown
                v-if="!mobile"
                :value="
                  ['price_asc', 'price_desc'].includes(order) ? order : null
                "
                :items="[
                  { label: '料金（安い順）', value: 'price_asc' },
                  { label: '料金（高い順）', value: 'price_desc' },
                ]"
                :disabled="isLoadingMaster"
                unselected-label="料金（安い順）"
                theme="flat"
                size="large"
                :width="148"
                icon="filled-spin"
                icon-color="blue"
                text-color="blue"
                bold
                :active="['price_asc', 'price_desc'].includes(order)"
                @change="updateOrder($event)"
              />
              <tk-button
                v-if="!mobile"
                type="button"
                :class="[
                  'tourListPage_sortButton',
                  '-hotelrank',
                  { '-active': order === 'hotelrank_desc' },
                ]"
                :disabled="isLoadingMaster"
                @click="updateOrder('hotelrank_desc')"
              >
                ホテルランク順
              </tk-button>
              <tk-button
                v-if="!mobile"
                type="button"
                :class="[
                  'tourListPage_sortButton',
                  { '-active': order === 'pageview' },
                ]"
                :disabled="isLoadingMaster"
                @click="updateOrder('pageview')"
              >
                人気順
              </tk-button>
            </div>
          </div>
        </div>
      </template>
    </tk-sticky-item>
    <div class="tourListPage_contentsArea">
      <div :class="{ tourListPageSplitContents: !mobile }">
        <div v-if="!mobile" class="tourListPage_leftContent">
          <tk-skeleton-loader
            v-if="!mobile && isLoadingTours"
            class="tourListPage_loadingFilterTile"
          />
          <tour-filter
            v-if="!mobile && !isLoadingTours"
            v-model="filterCondition"
            class="tourListPage_tourFilter"
            title="絞り込み"
            :campaigns="masterData.campaign"
            :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
            content-padding="12px 4px 16px"
            @filter="applyFilter"
          />
        </div>
        <div :class="[!mobile ? 'tourListPage_rightContent' : '']">
          <!-- 2022/4 より、vpBannerLink に明示的に空文字を入れて無効化している (OP のカレンダーより VP に流入できる状態のため不要との判断) -->
          <tk-banner
            v-if="vpBannerLink"
            class="tourListPage_vpBanner"
            target="_blank"
            :image-path="vpBannerImage"
            :href="vpBannerLink"
            alt="宿・航空チケットを自由に組み合わせ。"
          />
          <div v-if="!condition.isDomestic" class="tourListPage_listArea">
            <!-- ref は jest 用 -->
            <tk-declarative-tabs
              ref="tourTabs"
              nav-tabs-class="tourListPage_tourListTabs"
              active-class="active"
              content-class="tab-content"
              :active-tab-index="activeTourTabIndex"
              @select-tab="selectTourTab"
            >
              <template v-slot:title="{ title }">
                <!-- eslint-disable vue/no-v-html -->
                <div v-html="title"></div>
              </template>

              <!-- オンラインタブ -->
              <tk-tab :title="createTabTitle('WEB決済商品', totalOnlineTours)">
                <div class="tourListPage_listArea">
                  <tk-list-loading v-if="isLoadingTours" />
                  <tour-list
                    v-else-if="onlineTours.length > 0"
                    :tours="onlineTours"
                    :make-detail-page-url="makeDetailPageUrl"
                    :mobile="mobile"
                    :facets="condition.facets"
                  />
                  <!-- [暫定]ツアー0件の場合history back対応 開始
                  <order-made-link-cassette v-else />
                  -->
                  <tour-not-exist-cassette v-else />
                  <!-- [暫定]ツアー0件の場合history back対応 終了 -->
                </div>
                <div
                  v-if="!isLoadingTours && hasMoreOnlineTour"
                  class="tourListPage_displayMoreArea"
                >
                  <!-- ref は jest 用 -->
                  <tk-busy-cursor-button
                    ref="moreOnlineTours"
                    displayed-label="もっと見る"
                    :loading="isLoadingMore"
                    @pushed="fetchMoreTours(onlineTours)"
                  />
                </div>
              </tk-tab>

              <!-- オフラインタブ -->
              <tk-tab :title="createTabTitle('ツアー', totalOfflineTours)">
                <div class="tourListPage_listArea">
                  <tk-list-loading v-if="isLoadingTours" />
                  <tour-list
                    v-else-if="offlineTours.length > 0"
                    :tours="offlineTours"
                    :make-detail-page-url="makeDetailPageUrl"
                    :mobile="mobile"
                    :facets="condition.facets"
                    :duration-tabs="true"
                  />
                  <!-- [暫定]ツアー0件の場合history back対応 開始
                  <order-made-link-cassette v-else />
                  -->
                  <tour-not-exist-cassette v-else />
                  <!-- [暫定]ツアー0件の場合history back対応 終了 -->
                </div>
                <div
                  v-if="!isLoadingTours && hasMoreOfflineTour"
                  class="tourListPage_displayMoreArea"
                >
                  <!-- ref は jest 用 -->
                  <tk-busy-cursor-button
                    ref="moreOfflineTours"
                    displayed-label="もっと見る"
                    :loading="isLoadingMore"
                    @pushed="fetchMoreTours(offlineTours)"
                  />
                </div>
              </tk-tab>
            </tk-declarative-tabs>
          </div>
          <div v-else class="tourListPage_listArea">
            <div class="tourListPage_listArea">
              <tk-list-loading v-if="isLoadingTours" />
              <tour-list
                v-else-if="onlineTours.length > 0"
                :tours="onlineTours"
                :make-detail-page-url="makeDetailPageUrl"
                :mobile="mobile"
                :facets="condition.facets"
              />
              <!-- [暫定]ツアー0件の場合history back対応 開始
              <order-made-link-cassette v-else />
              -->
              <tour-not-exist-cassette v-else />
              <!-- [暫定]ツアー0件の場合history back対応 終了 -->
            </div>
            <div
              v-if="!isLoadingTours && hasMoreOnlineTour"
              class="tourListPage_displayMoreArea"
            >
              <!-- ref は jest 用 -->
              <tk-busy-cursor-button
                ref="moreOnlineTours"
                displayed-label="もっと見る"
                :loading="isLoadingMore"
                @pushed="fetchMoreTours(onlineTours)"
              />
            </div>
          </div>
        </div>
      </div>
      <tk-modal
        v-if="mobile && isFilterModalOpened"
        class="tourListPage_filterModal"
        :no-close-on-backdrop="true"
        @close="isFilterModalOpened = false"
      >
        <template slot="topbarTitle"> 絞り込み </template>
        <tour-filter
          v-model="filterCondition"
          size="x-large"
          :campaigns="masterData.campaign"
          :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
          content-padding="14px"
          sticky-button
          @filter="applyFilter"
        />
      </tk-modal>
      <tk-general-purpose-modal
        v-if="isSearchModalOpened"
        :no-close-on-backdrop="mobile"
        :no-close-on-esc="true"
        :no-sp-fullscreen="true"
        close-text="閉じる"
        @close="isSearchModalOpened = false"
      >
        <search-form
          v-model="editedSearchCondition"
          class="tourListPage_searchFormModal"
          size="xxx-large"
          passenger-number-unit="/1室"
          display-filter-condition
          :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
          theme="flat"
          simple-placeholder
          departure-icon
          destination-icon
          date-icon
          passenger-icon
          icon-color="red"
          icon-separator="default"
          @execute="searchAgain"
        />
      </tk-general-purpose-modal>
    </div>
  </div>
</template>

<script>
// import _isEqual from 'lodash/isEqual'
import '@/assets/css/list.scss'

import '@ota-frontend/shared-assets/css/font-awesome/css/font-awesome.min.css'
import {
  TkButton,
  TkBanner,
  TkBusyCursorButton,
  TkCustomPulldown,
  TkModal,
  TkPulldown,
  TkListLoading,
  TkSkeletonLoader,
  TkStickyItem,
  TkDeclarativeTabs,
  TkTab,
  TkGeneralPurposeModal,
} from '@ota-frontend/ui'
import { asNumber, compactSymbolObject } from '@ota-frontend/core/utils'
import { buildTourQueryFromSearchCondition, fetchBff } from '@/api'
import Breadcrumbs from '@/components/Breadcrumbs'
import FilterButton from '@/components/FilterButton'
import TourListHeader from '@/components/list/TourListHeader'
import SearchConditionBox from '@/components/SearchConditionBox'
import SearchForm from '@/components/SearchForm'
import { gtm, meta } from '@/mixins'
import { Areas, DeparturePlaces, Facets, SearchCondition } from '@/models'
import TourList from '@/modules/tour-list/components/TourList.vue'
import TourFilter from '@/modules/tour-list/components/TourFilter.vue'
/* [暫定]ツアー0件の場合history back対応 開始
import OrderMadeLinkCassette from '@/modules/tour-list/components/OrderMadeLinkCassette.vue'
*/
import TourNotExistCassette from '@/modules/tour-list/components/TourNotExistCassette.vue'
// [暫定]ツアー0件の場合history back対応 終了

// ツアーリストタブ一覧(順番はタブ順と一致させておくこと)
const TOURS_FOR_TABS = ['online', 'offline']

export default {
  name: 'TourListPage',

  metaInfo() {
    const noIndex = !(this.seoUrlPath && this.searchCondition.isEmpty)
    return this.__tourListMeta(this.condition, this.totalTours, noIndex)
  },

  components: {
    Breadcrumbs,
    FilterButton,
    SearchConditionBox,
    SearchForm,
    TkButton,
    TkBanner,
    TkBusyCursorButton,
    TkCustomPulldown,
    TkModal,
    TkPulldown,
    TkListLoading,
    TkSkeletonLoader,
    TkStickyItem,
    TourListHeader,
    TourList,
    TourFilter,
    TkDeclarativeTabs,
    TkTab,
    TkGeneralPurposeModal,
    /* [暫定]ツアー0件の場合history back対応
    OrderMadeLinkCassette,
    */
    TourNotExistCassette,
    // [暫定]ツアー0件の場合history back対応 終了
  },

  mixins: [gtm, meta],

  props: {
    mobile: {
      type: Boolean,
      required: true,
    },
    searchCondition: {
      type: SearchCondition,
      required: true,
    },
    // props に渡される検索条件を更新するための関数
    updateSearchCondition: {
      type: Function, // SearchCondition => {}, props の更新を促すための関数
      required: true,
    },
    // ツアー詳細画面へのリンクを生成する関数
    makeDetailPageUrl: {
      type: Function, // saleType => tourCode => url
      required: true,
    },
    // 現在の検索条件を持った状態でツアーTOPへ戻るためのURLをつくる関数
    makeSearchAgainUrl: {
      type: Function,
      required: true,
    },
    /**
     * /japan/tour/hokkaido/sapporo 等の seo URL の場合は
     * これを initial-display BFF に渡すことで都市コード等を解決してもらう
     * ページコンポーネント的には内容に依存した処理は一切しない想定
     */
    seoUrlPath: {
      type: String,
      default: '',
    },
    /**
     * 検索表示出来るツアーの最大件数
     * Elasticsearchの仕様により現状検索APIが返せる最大件数は10000件
     */
    maxNumberOfToursToDisplay: {
      type: Number,
      default: 10000,
    },
    // デフォルト選択タブ(online: オンラインタブ, offline: オフラインタブ)
    defaultTab: {
      type: String,
      default: 'online',
    },
    // 着地時に再検索フォームを表示するかどうか
    showResearch: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoadingMaster: false, // 現状、初期表示BFF待ち合わせのタイミングのみ true となる
      isLoadingTours: false,
      isLoadingMore: false,
      isFilterModalOpened: false,
      isSearchModalOpened: this.showResearch,
      editedSearchCondition: this.searchCondition.empty,
      filterCondition: this.searchCondition.empty,
      onlineTours: [],
      offlineTours: [],
      hasOnlineTour: false,
      vpBannerLink: '',
      masterData: {},
      areas: new Areas(),
      totalOnlineTours: 0,
      totalOfflineTours: 0,
      departurePlaces: new DeparturePlaces(),
      allFacets: new Facets(),
      tours: [],
      totalTours: 0,

      // /japan/tour/hokkaido/sapporo 等のURLの場合、domestic, city_id 等がURLに乗らない仕様のため、
      // パスのパラメータからそれらを解決した情報が必要になる
      // initial-display の BFF が返すそれらをここに保存し、デフォルト検索条件として暗黙的に使用する
      seoSearchCondition: {},
      activeTourTab:
        TOURS_FOR_TABS.find((t) => t === this.defaultTab) || 'online',

      // 選択不可の検索条件を外す処理の無限ループを確実に回避するためのフェールセーフ
      // intention_ids=111,222 と 222,111 を行き来するなどの異常が起きた場合に強制的にロケーション上書きをストップする
      // Sentry 等を導入し、万一これが効力を発揮したら検知できるようにしておくべき
      justNormalizedCondition: false,
    }
  },

  computed: {
    condition() {
      return this.searchCondition
        .withDefaults(
          compactSymbolObject({
            [SearchCondition.PARAMS.DOMESTIC]: this.seoSearchCondition.domestic,
            [SearchCondition.PARAMS.COUNTRY_OF_DESTINATION1]: asNumber(
              this.seoSearchCondition.country_id
            ),
            [SearchCondition.PARAMS.CITY_OF_DESTINATION1]: asNumber(
              this.seoSearchCondition.city_id
            ),
            [SearchCondition.PARAMS.DISTRICT_OF_DESTINATION1]: asNumber(
              this.seoSearchCondition.district_id
            ),
            // 静的URLで着地した直後の状態の場合（あるいは普通に検索条件が全く指定されていない場合）、seoSearchCondition に keyword がある場合はそれを採用する
            [SearchCondition.PARAMS.KEYWORD]: this.searchCondition.withoutMeta
              .isEmpty
              ? this.seoSearchCondition.keyword
              : this.searchCondition.keyword,
          })
        )
        .withAreas(this.areas)
        .withDeparturePlaces(this.departurePlaces)
        .withFacets(this.allFacets)
    },
    order() {
      return this.condition.order || this.selectableOrders[0].value
    },
    selectableOrders() {
      return SearchCondition.ORDERS.map((o) => ({
        label: o.name,
        value: o.code,
      }))
    },
    vpBannerImage() {
      return this.mobile
        ? require('@ota-frontend/shared-assets/images/tour/tour-free_pr.png')
        : require('@ota-frontend/shared-assets/images/tour/tour-free_pr695.png')
    },
    urlForSearchAgain() {
      return this.makeSearchAgainUrl(this.condition)
    },
    activeTourTabIndex() {
      return TOURS_FOR_TABS.findIndex((t) => t === this.activeTourTab)
    },
    hasMoreOnlineTour() {
      return this.totalOnlineTours > this.onlineTours.length
    },
    hasMoreOfflineTour() {
      return this.totalOfflineTours > this.offlineTours.length
    },
    // watch によるツアー一覧更新検知用
    allTours() {
      return [...this.onlineTours, ...this.offlineTours]
    },
  },

  watch: {
    searchCondition(newCondition, oldCondition) {
      if (newCondition.basicConditionIsDifferentFrom(oldCondition)) {
        this.fetchInitialDisplayInfo()
      } else {
        this.fetchTours()
      }
      this.isFilterModalOpened = false
      this.isSearchModalOpened = false
      this.editedSearchCondition = this.condition.clone()
      this.filterCondition = this.condition.clone()
    },
    allTours() {
      if (this.$gtm && this.$gtm.enabled()) {
        this.$gtm.trackEvent({
          event: 'custom.static-tour.list',
          ...this.__gtmDataLayerOnNewTourList(this.condition),
        })
      }
    },
  },

  async beforeMount() {
    this.editedSearchCondition = this.condition.clone()
    await this.fetchInitialDisplayInfo()
  },

  methods: {
    async fetchInitialDisplayInfo() {
      this.isLoadingMaster = true
      this.isLoadingTours = true

      const condition = this.condition
      const initialDisplayInfo = await fetchBff('tour/list/initial-display', {
        ...buildTourQueryFromSearchCondition(condition),
        url_path: this.seoUrlPath,
      })
      const { tours, total } = initialDisplayInfo.tours || {}
      this.tours = tours || []
      this.totalTours = total || 0
      // await 後、ヒストリーバック等で検索条件が変わっていることがあり、その場合はこの関数自体をキャンセルする
      if (this.condition !== condition) return

      // 新しい構造に合わせてデータを取得
      const { tours: onlineTours = [], total: onlineTotal = 0 } =
        initialDisplayInfo.tours || {}
      const { tours: offlineTours = [], total: offlineTotal = 0 } =
        initialDisplayInfo.offline || {}

      this.onlineTours = onlineTours
      this.offlineTours = offlineTours
      this.tours = onlineTours
      this.vpBannerLink = ''
      this.masterData = initialDisplayInfo.master_data || {}
      this.areas = new Areas(
        (initialDisplayInfo.master_data &&
          initialDisplayInfo.master_data.area) ||
          []
      )
      this.departurePlaces = new DeparturePlaces(
        initialDisplayInfo.master_data &&
        initialDisplayInfo.master_data.departure
          ? [initialDisplayInfo.master_data.departure]
          : []
      )
      this.totalOnlineTours = onlineTotal
      this.totalOfflineTours = offlineTotal
      this.allFacets = new Facets(initialDisplayInfo.all_facets || {})
      this.seoSearchCondition = initialDisplayInfo.seo_search_conditions || {}

      // 現在の検索条件で本来選択できない絞り込みが指定されている場合、解除してロケーションの上書き修正を促す
      // この部分は fetchInitialDisplayInfo の中で実施するべきではないかもしれない
      const unselectableConditionsRemoved = this.condition.withoutUnselectables
      if (
        !unselectableConditionsRemoved.isEqualTo(this.condition) &&
        !this.justNormalizedCondition
      ) {
        this.updateSearchCondition(unselectableConditionsRemoved, {
          normalizeLocation: true,
          replace: true,
        })
        this.isLoadingMaster = false
        this.isLoadingTours = false
        this.justNormalizedCondition = true
        return
      }

      this.editedSearchCondition = this.condition.clone()
      this.filterCondition = this.condition.clone()

      if (
        this.activeTourTab === 'online' &&
        this.onlineTours.length === 0 &&
        this.offlineTours.length > 0
      ) {
        this.activeTourTab = 'offline'
      } else if (
        this.activeTourTab === 'offline' &&
        this.offlineTours.length === 0 &&
        this.onlineTours.length > 0
      ) {
        this.activeTourTab = 'online'
      }

      this.isLoadingMaster = false
      this.isLoadingTours = false
      this.justNormalizedCondition = false
    },
    async fetchTours() {
      this.isLoadingTours = true

      const condition = this.condition
      const toursData = await fetchBff(
        'tour/list/tours',
        buildTourQueryFromSearchCondition(condition)
      )
      const { tours, total } = toursData.tours || {}
      this.tours = tours || []
      this.totalTours = total || 0
      // await 後、ヒストリーバック等で検索条件が変わっていることがあり、その場合はこの関数自体をキャンセルする
      if (this.condition !== condition) return

      // 新しい構造に合わせてデータを取得
      const { tours: onlineTours = [], total: onlineTotal = 0 } =
        toursData.tours || {}
      const { tours: offlineTours = [], total: offlineTotal = 0 } =
        toursData.offline || {}

      this.onlineTours = onlineTours
      this.offlineTours = offlineTours
      this.tours = [...onlineTours, ...offlineTours]
      this.totalOnlineTours = onlineTotal
      this.totalOfflineTours = offlineTotal

      this.isLoadingTours = false
      this.justNormalizedCondition = false
    },
    async fetchMoreTours(targetTours) {
      this.isLoadingMore = true

      const condition = this.condition
      const moreTours = await fetchBff('tour/list/more-tours', {
        ...buildTourQueryFromSearchCondition(condition),
        start: String(targetTours.length), // TODO: こういったものは BFF のクライアントモジュール側に書く
      })
      // await 後、ヒストリーバック等で検索条件が変わっていることがあり、その場合はこの関数自体をキャンセルする
      if (this.condition !== condition) return

      targetTours.push(...moreTours.tours)
      this.isLoadingMore = false
    },
    searchAgain() {
      this.updateSearchCondition(this.editedSearchCondition, {
        normalizeLocation: true,
        passiveParameters: {
          research: 1, // 計測用パッシブパラメータ
        },
      })
      this.isSearchModalOpened = false
    },
    applyFilter() {
      this.updateSearchCondition(
        this.searchCondition.withoutFilters.update(
          this.filterCondition.filters.raw
        )
      )
      this.isFilterModalOpened = false
    },
    updateOrder(order) {
      if (!this.searchCondition.order && order === 'price_fav') return

      this.updateSearchCondition(
        this.searchCondition.update({
          [SearchCondition.PARAMS.ORDER]: order,
        })
      )
    },
    selectTourTab({ tabIndex }) {
      this.activeTourTab = TOURS_FOR_TABS[tabIndex]
    },
    formatTotalTours(total) {
      if (this.isLoadingTours) {
        return '（- 件）'
      }
      return this.maxNumberOfToursToDisplay < total
        ? `（${this.maxNumberOfToursToDisplay.toLocaleString()}件以上）`
        : `（${total.toLocaleString()} 件）`
    },
    createTabTitle(title, total) {
      return `<span class='tourListPage_tourListTabTitle'>${title}</span>
              <br>${this.formatTotalTours(total)}`
    },
  },
}
</script>

<style scoped lang="scss">
.tourListPage {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.tourListPage_breadcrumbsArea {
  margin: 0 auto;
  width: 100%;
  max-width: 980px;
  padding: 10px 10px 0 10px;
  white-space: nowrap;
  font-size: 11px;
  background: #f4f4f4;
}
.tourListPage_searchConditionBoxWrapper {
  width: 100%;
}
.tourListPage_searchConditionBox {
  width: 100%;
  max-width: 960px;
  border-top: 1px solid #dfdfdf;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);

  &.-pinned {
    border-top: none;
    border-bottom: 1px solid #dfdfdf;
    box-shadow: none;
  }
}
.tourListPage_searchFormAreaWrapper {
  width: 100%;
}
.tourListPage_searchFormArea {
  width: 100%;
  padding: 8px 0;
  border-top: 1px solid #dcdcdc;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);
  background-color: white;

  &.-pinned {
    border-top: none;
    border-bottom: 1px solid #dcdcdc;
    box-shadow: none;
  }
}
.tourListPage_searchFormLoading {
  display: block;
  width: 960px;
  height: 50px;
  margin: auto;
}
.tourListPage_searchForm {
  width: 964px;
  margin: 0 auto;
}
.tourListPage_sortAreaWrapper {
  width: 100%;
}
.tourListPage_sortArea {
  width: 100%;
  padding: 8px 0 6px;
  background-color: #f2f2f2;

  &.-pinned {
    background-color: white;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);
  }
}
.tourListPage_sortAreaInner {
  justify-content: space-between;
  align-items: center;
  width: 960px;
  margin: 0 auto;
  padding-left: 10px;

  @media (max-width: 657px) {
    width: 100%;
    padding: 0 8px;
  }
}
.tourListPage_sortButtons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tourListPage_sortButton {
  width: 114px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #bbd2e6;
  border-radius: 4px;
  background: white;
  color: #1f6aad;
  font-size: 15px;
  font-weight: bold;
  line-height: 36px;
  cursor: pointer;

  &.-hotelrank {
    width: 140px;
  }

  &.-active {
    border: none;
    background: #1f6aad;
    color: #ffffff;
  }

  &:focus {
    outline: 0;
  }
}
.tourListPage_sortPulldown {
  max-width: 144px; // 375px のデバイスでソート部分全体がギリギリ改行無しで入るサイズ
}
.tourListPage_linkForSearchAgain {
  display: flex;
  gap: 10px;
  margin-top: 6px;

  @media (max-width: 657px) {
    align-items: start;
    margin-top: 0;
    gap: 4px;
  }
}
.tourListPage_linkForSearchAgainArrow {
  width: 6px;
  margin-top: -2px;

  @media (max-width: 657px) {
    margin-top: 3px;
  }
}
.tourListPage_linkForSearchAgainText {
  color: #1f6aad;
  text-decoration: underline;
}
.tourListPage_contentsArea {
  max-width: 960px;
  margin: 12px auto 0;

  @media (max-width: 657px) {
    width: 100%;
    max-width: 657px;
    margin: 0 auto 0;
  }
}
.tourListPageSplitContents {
  display: flex;
  margin: 0 10px;
  gap: 30px;
}
.tourListPage_leftContent {
  width: 300px;
}
.tourListPage_loadingFilterTile {
  display: block;
  position: relative;
  height: 1000px;
  margin-bottom: 10px;
}
.tourListPage_rightContent {
  width: 620px;
}
.tourListPage_vpBanner {
  text-align: center;
  margin: 5px 0 17px auto;
  border: 1px solid #d2d2d2;
  border-radius: 8px;

  @media (max-width: 657px) {
    padding: 0 10px;
    border: none;
    border-radius: 0;
  }
}
.tourListPage_listArea {
  padding: 15px 15px 5px;
  background: #f2f2f2;

  @media (max-width: 657px) {
    padding: 10px 10px 5px;
  }
}
.tourListPage_searchFormModal {
  border-radius: 0 0 8px 8px;
}
.tourListPage_ordermadeArea {
  width: 100%;
  border-top: 1px #cdcdcd solid;
  background-color: #f5f5f4;
  margin-top: 25px;

  @media (max-width: 657px) {
    margin-top: 0;
  }
}
.tourListPage_ordermadeHead {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  padding: 26px 0 21px;
}
.tourListPage_ordermadeUnderArrow {
  margin-top: 7px;
}
.tourListPage_displayMoreArea {
  padding-bottom: 15px;
  margin: 0 auto;
  text-align: center;
  box-sizing: border-box;
  background: #f2f2f2;
}
</style>

<style lang="scss">
.modal-open .modal {
  overflow-x: hidden;
  overflow-y: auto;
}
.modal {
  position: fixed;
  padding-right: 0 !important;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  outline: 0;
}
.modal-content {
  position: relative;
  background-color: #fff;
}
.tourListPage_filterModal {
  .tourHotelPlaceModal_topbar {
    text-align: left;
    border-radius: 0;
    color: #fff;
    background-color: #63666a;
    width: 100%;
    padding: 8px 14px;
    position: relative;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    white-space: nowrap;
    line-height: 1.42857143;
    vertical-align: middle;
    height: auto;
  }
  .tourHotelPlaceModal_title {
    font-size: 17px;
    text-decoration: none;
    -webkit-user-select: none;
    user-select: none;
  }
  .btn.btn-link.tourHotelPlaceModal_close {
    padding: 0;
    margin-bottom: 0;
    touch-action: manipulation;
    cursor: pointer;
    background-image: none;
    border: 1px solid rgba(0, 0, 0, 0);
    min-height: auto;
  }
}
.tourListPage_tourListTabs {
  display: flex;
  width: 100%;
  padding: 0 5px;
  border-bottom: solid 1px #d2d2d2;
  position: relative;
  justify-content: space-between;

  & li {
    border-radius: 4px 4px 0 0;
    background: #e7e7e7;
    border: 1px solid #d2d2d2;
    border-bottom: none;
    justify-content: center;
    padding: 10px 0;
    width: 100%;
    font-size: 13px;
    float: left;
    cursor: pointer;
    text-align: center;

    &.active {
      background: #f2f2f2;
      color: #333;
      border-bottom: none;
      font-weight: bold;
      position: relative;
      top: 1px;
    }
  }
}
.tourListPage_totalCountNumber{
  display: inline-block;
  margin: 0 5px 0 0;
  font-size: 20px;
  font-weight: 700;
}

.tourListPage_tourListTabTitle {
  font-size: 16px;
}
.tourListPage_tourListTabs {
  display: none;
}
</style>
